import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, untracked, ViewEncapsulation } from '@angular/core';

import { CreateEntityConfigService } from './config/create-entity-config.service';
import { CreateEntityWidgetConfig } from './config/create-entity-widget-config.types';
import { CREATE_ENTITY_EVENTS, ENTITY_TAB_LABELS, EntityTab, NOT_CONFIGURED_MESSAGE } from './create-entity-element.types';
import { CreateEntityCommandService } from './create-entity-command.service';
import { CreateEntityReferenceDataService } from './create-entity-reference-data.service';
import { CreateEntityWizardStore } from './create-entity-wizard.store';
import { FacilityFormComponent } from './facility/facility-form.component';
import { InvestigatorPanelComponent } from './investigator-panel.component';
import { OrganizationFormComponent } from './organization/organization-form.component';
import { resolveVisibleTabs } from './shared/visible-tabs.util';
import { StudyFormComponent } from './study/study-form.component';

/**
 * Self-contained Angular Elements Component for entity creation.
 *
 * Ships with its own Angular runtime and is registered as `<wcg-create-entity>`
 * via `createCustomElement` in `main.ts`. Uses `ViewEncapsulation.ShadowDom` so
 * its styles never leak into (or are leaked into by) the host page — the same
 * isolation boundary a real `<script src="...">`-loaded widget would have.
 *
 * UI is a set of peer tabs (Organization / Facility / Study / Investigator) rather than
 * a linear wizard — switching tabs never loses data already entered in another
 * tab.
 *
 * This component holds no reference to its child forms (no `viewChild`) and has no
 * knowledge of any form's internal shape. All cross-tab coordination — which tab is
 * active, "Save"/"+ Add Another" commands, and the created-entity result — flows through
 * `CreateEntityWizardStore`, injected here and by each `*FormComponent` sibling. Each
 * tab's form lives in its own sub-component (`OrganizationFormComponent`,
 * `FacilityFormComponent`, `StudyFormComponent`, `InvestigatorPanelComponent`) and owns
 * validating itself, building its own request body, and calling its own API service. They
 * render inside this component's single ShadowDom root (child components use the default
 * Emulated encapsulation, so they don't get a shadow root of their own and continue to
 * share `create-entity-element.component.css`).
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'wcg-create-entity',
    imports: [OrganizationFormComponent, FacilityFormComponent, StudyFormComponent, InvestigatorPanelComponent],
    providers: [CreateEntityCommandService, CreateEntityConfigService, CreateEntityReferenceDataService, CreateEntityWizardStore],
    encapsulation: ViewEncapsulation.ShadowDom,
    styleUrl: './create-entity-element.component.css',
    templateUrl: './create-entity-element.component.html'
})
export class CreateEntityElementComponent {
    private readonly _elementRef = inject(ElementRef<HTMLElement>);
    private readonly _config = inject(CreateEntityConfigService);
    protected readonly wizard = inject(CreateEntityWizardStore);
    private _reportedUnusableConfig = false;

    /**
     * The host's auth contract: set as attributes (`access-token`,
     * `event-source`, `jemma-base-url`) or DOM properties. Assigning a new `accessToken` to a live
     * element hands the widget a refreshed token; the next call uses it. `eventSource` and `jemmaBaseUrl` have no defaults:
     * without them the widget shows an error and makes no calls, rather than borrowing another app's ID or calling the wrong environment.
     */
    readonly accessToken = input<string>('');
    readonly eventSource = input<string>('');
    readonly jemmaBaseUrl = input<string>('');

    /**
     * Optional. Which tabs to show: an array (`el.tabs = ['organization', 'study']`) or a comma-separated
     * attribute (`tabs="organization,study"`). Defaults to every tab. Tabs always appear in the widget's
     * fixed order, and unknown or empty values silently fall back to every tab.
     */
    readonly tabs = input<string | readonly string[] | null>(null);

    protected readonly tabLabels = ENTITY_TAB_LABELS;
    protected readonly visibleTabs = computed(() => resolveVisibleTabs(this.tabs()));
    readonly $config = computed<CreateEntityWidgetConfig>(() => ({
        accessToken: this.accessToken(),
        eventSource: this.eventSource(),
        jemmaBaseUrl: this.jemmaBaseUrl()
    }));

    constructor() {
        effect(() => {
            const _tabs = this.visibleTabs();
            untracked(() => this.wizard.setVisibleTabs(_tabs));
        });

        effect(() => {
            const _config = this.$config();
            untracked(() => this._syncConfig(_config));
        });

        // Dispatches entity-created exactly once per newly created entity, mapping the wizard's internal `{ kind, data }` shape to the host-facing event detail contract (`{ organization }` / `{ facility }` / `{ study }`).
        effect(() => {
            const _result = this.wizard.result();

            if (_result) {
                untracked(() => this._dispatch(CREATE_ENTITY_EVENTS.created, { [_result.kind]: _result.data }));
            }
        });
    }

    /** Switches the visible tab without touching any form's entered values. */
    selectTab(tab: EntityTab): void {
        this.wizard.selectTab(tab);
    }

    /** Cancels the whole flow. Notifies the host so it can close/dismiss the surrounding modal. */
    cancel(): void {
        this._dispatch(CREATE_ENTITY_EVENTS.cancelled);
    }

    /** Tells whichever `*FormComponent` matches the active tab to validate and submit itself. */
    submit(): void {
        this.wizard.requestSubmit();
    }

    /** Resets the form for whichever entity was just created and returns to that same tab, per the "+ Add Another" CTA on the success screen. */
    addAnother(): void {
        this.wizard.addAnother();
    }

    /** Dismisses the widget after a successful creation. */
    done(): void {
        this._dispatch(CREATE_ENTITY_EVENTS.closed);
    }

    private _syncConfig(config: CreateEntityWidgetConfig): void {
        this._config.setConfig(config);

        if (this._config.$isUsable()) {
            this._reportedUnusableConfig = false;
            if (this.wizard.errorMessage() === NOT_CONFIGURED_MESSAGE) {
                this.wizard.setConfigErrorMessage(null);
            }
            return;
        }

        // A host that sets the inputs right after attaching the element (an Angular template, for example) gets one tick before the widget calls the config unusable.
        setTimeout(() => this._reportUnusableConfig());
    }

    private _reportUnusableConfig(): void {
        if (this._config.$isUsable() || this._reportedUnusableConfig) {
            return;
        }

        this._reportedUnusableConfig = true;
        this.wizard.setConfigErrorMessage(NOT_CONFIGURED_MESSAGE);
        this._dispatch(CREATE_ENTITY_EVENTS.failed);
    }

    private _dispatch(type: string, detail?: unknown): void {
        this._elementRef.nativeElement.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
    }
}
